import { isDistributorAdmin } from '../utils/roles';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PageHeader from '../components/PageHeader';
import PrimaryButton from '../components/PrimaryButton';
import NewUserModal from '../components/NewUserModal';
import userService from '../services/userService';
import clinicService from '../services/clinicService';
import distributorService from '../services/distributorService';
import { captureSession, isSessionCurrent } from '../utils/authSession';
import { useT } from '../i18n/LanguageContext';

/**
 * WHO CAN GET INTO THIS APP, AND WHAT THEY CAN SEE (CL-260824-06).
 *
 * Admin-only by construction: /users is not in REP_ALLOWED_PATHS, so App.js wraps it in
 * RequireAdmin automatically. Every endpoint behind it is @Roles('admin') server-side too.
 *
 * This is deliberately NOT just a list of names and emails. The thing that actually matters about a
 * MedEffects login is its BLAST RADIUS - which patients' records it can reach - and that is decided
 * by two fields the old UI never showed:
 *
 *   - a `clinic` login is bound to exactly ONE clinic, and buildTenantReadFilter returns NOTHING
 *     when that binding is missing. An unbound clinic account is not a small misconfiguration; the
 *     user logs in successfully and sees an empty app with no explanation.
 *   - a `sales_rep` sees the clinics assigned to them. With none assigned they see only their own
 *     submissions - which is exactly how Kevin Wood sat until 8/24/26, looking fine in every list
 *     while covering 14 practices he could not see.
 *
 * So the Access column resolves ids to real clinic names and calls out both cases in red. A row
 * that reads "no clinic bound" is a broken login, not a styling choice.
 */

const ROLES = ['admin', 'sales_rep', 'master_distributor', 'clinic', 'patient'];

const ROLE_STYLE = {
  admin: 'bg-purple-50 border-purple-200 text-purple-700',
  sales_rep: 'bg-blue-50 border-blue-200 text-blue-700',
  master_distributor: 'bg-teal-50 border-teal-200 text-teal-700',
  clinic: 'bg-green-50 border-green-200 text-green-700',
  patient: 'bg-slate-100 border-slate-200 text-slate-600',
};

const ROLE_LABEL = {
  admin: 'Admin',
  sales_rep: 'Sales Rep',
  master_distributor: 'Master distributor',
  clinic: 'Clinic',
  patient: 'Patient',
};

const refId = (v) => (v && typeof v === 'object' ? v._id || v.id : v);

export default function Users() {
  const t = useT();
  const [users, setUsers] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [clinicsById, setClinicsById] = useState({});
  // + NEW USER. The dialog creates the login; the banner afterwards is the one place the password is
  // shown, because the admin has to hand it to the person and the server never returns it again.
  const [showNew, setShowNew] = useState(false);
  const [created, setCreated] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [role, setRole] = useState('');
  const [businessContext, setBusinessContext] = useState('all');
  const [distributorId, setDistributorId] = useState('');
  const [distributors, setDistributors] = useState([]);
  const [distributorError, setDistributorError] = useState(false);
  const [orgReload, setOrgReload] = useState(0);
  // Active by default. A deactivated user is locked out but often CANNOT be deleted - see the note
  // on the status filter below - so the default view is "who can actually get in".
  const [active, setActive] = useState('true');
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const requestSequence = useRef(0);

  const LIMIT = 25;

  // Clinic names are not populated by /admin/users - it returns raw ids. Fetched once and joined
  // here rather than per row.
  useEffect(() => {
    clinicService
      .adminList()
      .then((res) => {
        const rows = Array.isArray(res.data?.data) ? res.data.data : res.data?.data?.data || [];
        const map = {};
        rows.forEach((c) => {
          map[String(c._id)] = c.name;
        });
        setClinics(rows);
        setClinicsById(map);
      })
      .catch(() => {
        setClinics([]);
        setClinicsById({});
      });
  }, []);

  const needsDistributors =
    businessContext === 'distributor' || users.some((user) => user.distributor);
  useEffect(() => {
    if (!needsDistributors) return;
    let current = true;
    const session = captureSession();
    Promise.resolve()
      .then(() => distributorService.adminDistributors())
      .then((response) => {
        if (!current || !isSessionCurrent(session)) return;
        const rows = response?.data?.data;
        if (!Array.isArray(rows) || rows.some((row) => !row._id || typeof row.name !== 'string'))
          throw new Error('Invalid distributors');
        setDistributors(rows);
        setDistributorError(false);
      })
      .catch(() => {
        if (current && isSessionCurrent(session)) {
          setDistributors([]);
          setDistributorError(true);
        }
      });
    return () => {
      current = false;
    };
  }, [orgReload, needsDistributors]);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebounced(search.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    const request = ++requestSequence.current;
    const session = captureSession();
    const current = () => request === requestSequence.current && isSessionCurrent(session);
    setLoading(true);
    setUsers([]);
    setTotal(0);
    setTotalPages(1);
    setError('');
    try {
      // Server orders the complete result before pagination: Admin, Sales Rep, then Clinic.
      const params = { page, limit: LIMIT, sort: 'role' };
      if (businessContext !== 'all') params.businessContext = businessContext;
      if (businessContext === 'distributor' && distributorId) params.distributorId = distributorId;
      if (role) params.role = role;
      if (active) params.isActive = active === 'true';
      if (debounced) params.search = debounced;
      const res = await userService.getUsers(params);
      if (!current()) return;
      const body = res.data?.data ?? {};
      setUsers(Array.isArray(body.data) ? body.data : []);
      setTotal(body.total ?? 0);
      setTotalPages(body.totalPages ?? 1);
    } catch (err) {
      if (!current()) return;
      setError(
        // English in state, translated where it is shown ({t(error)}): keeping `t` out of this
        // callback means a language switch does not refetch the list.
        'Could not load users.'
      );
      setUsers([]);
    } finally {
      if (current()) setLoading(false);
    }
  }, [page, role, active, debounced, businessContext, distributorId]);

  useEffect(() => {
    load();
    return () => {
      requestSequence.current += 1;
    };
  }, [load]);

  const clinicName = useCallback((id) => clinicsById[String(refId(id))] || null, [clinicsById]);

  /** What this login can reach. Returns { text, broken } - broken rows are the point of the page. */
  const access = useCallback(
    (u) => {
      if (isDistributorAdmin(u))
        return { text: t('All distributors; no client workspace access'), broken: false };
      if (u.role === 'admin') return { text: t('Everything'), broken: false };
      if (u.distributor) {
        const organization = distributors.find((row) => row._id === String(refId(u.distributor)));
        const name = organization?.name || t('Distributor');
        if (u.role === 'master_distributor')
          return {
            text: t('{name}: distributor customers and product bank', { name }),
            broken: false,
          };
        return {
          text: t('{name}: own distributor clinic records only', { name }),
          broken: !refId(u.clinic),
        };
      }
      if (u.role === 'master_distributor')
        return { text: t('No distributor bound - review this login'), broken: true };
      if (u.role === 'clinic') {
        const id = refId(u.clinic);
        if (!id) return { text: t('No clinic bound - this login sees nothing'), broken: true };
        const name = clinicName(id);
        const scope = u.clinicRole === 'user' ? ' ' + t('(own submissions only)') : '';
        return { text: (name || t('Unknown clinic')) + scope, broken: !name };
      }
      if (u.role === 'sales_rep') {
        const ids = (u.clinics || []).map(refId).filter(Boolean);
        if (!ids.length)
          return { text: t('No clinics assigned - sees only own submissions'), broken: true };
        const names = ids.map((i) => clinicName(i)).filter(Boolean);
        return {
          text: names.length <= 2 ? names.join(', ') : t('{n} clinics', { n: names.length }),
          title: names.join(', '),
          broken: false,
        };
      }
      return { text: t('Own records only'), broken: false };
    },
    [clinicName, t, distributors]
  );

  const brokenCount = useMemo(() => users.filter((u) => access(u).broken).length, [users, access]);

  /** The dialog saved. Return to page 1, Active, in role order, and show the credentials once. */
  const handleCreated = (user) => {
    setShowNew(false);
    setCreated(user);
    setSearch('');
    setDebounced('');
    setRole('');
    setBusinessContext('all');
    setDistributorId('');
    setActive('true');
    setPage(1);
    load();
  };

  return (
    <div>
      <PageHeader title="Users" subtitle="Every login, its role, and exactly what it can see.">
        <PrimaryButton onClick={() => setShowNew(true)}>{t('+ New User')}</PrimaryButton>
      </PageHeader>

      {created && (
        <div
          role="status"
          className="mb-4 p-4 rounded-lg text-sm bg-green-50 border border-green-200 text-green-800 flex items-start justify-between gap-4"
        >
          <div>
            <p className="font-semibold">
              {t('{name} can sign in now.', { name: `${created.firstName} ${created.lastName}` })}
            </p>
            <p className="mt-1">
              {t('Email')} <span className="font-mono">{created.email}</span> · {t('Password')}{' '}
              <span className="font-mono">{created.password}</span>
            </p>
            <p className="mt-1 text-[12px] text-green-700">
              {t('Copy the password now. It is not shown again.')}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setCreated(null)}
            aria-label={t('Dismiss')}
            className="text-green-700 hover:text-green-900 shrink-0"
          >
            <span className="material-symbols-outlined text-[20px]">close</span>
          </button>
        </div>
      )}

      {showNew && (
        <NewUserModal
          clinics={clinics}
          onClose={() => setShowNew(false)}
          onCreated={handleCreated}
        />
      )}

      <div className="bg-white border border-[#e2e8f0] rounded-[14px] shadow-sm p-6">
        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-5">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('Search name or email...')}
            aria-label={t('Search users')}
            className="h-[42px] px-4 border border-[#d6dce8] rounded-[8px] text-sm text-[#24315d] w-[280px] focus:outline-none focus:ring-2 focus:ring-[#0089ff]/20 focus:border-[#0089ff]"
          />
          <select
            value={businessContext}
            onChange={(event) => {
              setBusinessContext(event.target.value);
              setDistributorId('');
              setPage(1);
            }}
            aria-label={t('Filter by portal')}
            className="h-[42px] px-3 border border-[#d6dce8] rounded-[8px] text-sm"
          >
            <option value="all">{t('All portals')}</option>
            <option value="direct">{t('Direct MedEffects')}</option>
            <option value="distributor">{t('Distributor')}</option>
          </select>
          {businessContext === 'distributor' && (
            <select
              value={distributorId}
              onChange={(event) => {
                setDistributorId(event.target.value);
                setPage(1);
              }}
              disabled={distributorError || !distributors.length}
              aria-label={t('Filter by distributor')}
              className="h-[42px] px-3 border border-[#d6dce8] rounded-[8px] text-sm max-w-full"
            >
              <option value="">{t('All distributors')}</option>
              {distributors.map((row) => (
                <option key={row._id} value={row._id}>
                  {row.name}
                </option>
              ))}
            </select>
          )}
          <select
            value={role}
            onChange={(e) => {
              setRole(e.target.value);
              setPage(1);
            }}
            aria-label={t('Filter by role')}
            className="h-[42px] px-3 border border-[#d6dce8] rounded-[8px] text-sm text-[#24315d] focus:outline-none focus:ring-2 focus:ring-[#0089ff]/20 focus:border-[#0089ff]"
          >
            <option value="">{t('All roles')}</option>
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {t(ROLE_LABEL[r])}
              </option>
            ))}
          </select>
          <select
            value={active}
            onChange={(e) => {
              setActive(e.target.value);
              setPage(1);
            }}
            aria-label={t('Filter by status')}
            className="h-[42px] px-3 border border-[#d6dce8] rounded-[8px] text-sm text-[#24315d] focus:outline-none focus:ring-2 focus:ring-[#0089ff]/20 focus:border-[#0089ff]"
          >
            <option value="true">{t('Active')}</option>
            <option value="false">{t('Deactivated')}</option>
            <option value="">{t('All')}</option>
          </select>
          <span className="text-[13px] text-[#64748b] ml-auto">
            {loading ? t('Loading...') : t(total === 1 ? '{n} user' : '{n} users', { n: total })}
            {brokenCount > 0 && (
              <span className="ml-2 text-[#de524c] font-medium">
                {t('{n} need attention on this page', { n: brokenCount })}
              </span>
            )}
          </span>
        </div>

        {distributorError && businessContext === 'distributor' && (
          <p role="alert" className="mb-4 text-sm text-red-700">
            {t(
              'Distributor names are unavailable. The list still uses the selected portal filter.'
            )}{' '}
            <button
              type="button"
              className="underline"
              onClick={() => setOrgReload((value) => value + 1)}
            >
              {t('Retry')}
            </button>
          </p>
        )}
        {error && (
          <div className="mb-4 p-3 rounded-lg text-sm bg-red-50 border border-red-200 text-red-600">
            {t(error)}
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px]">
            <thead>
              <tr className="text-left">
                {['USER', 'EMAIL', 'ROLE', 'PORTAL', 'CAN SEE', 'ACTIONS'].map((c) => (
                  <th
                    key={c}
                    className="text-[11px] font-semibold text-[#64748b] uppercase tracking-wider pb-3 border-b border-[#e2e8f0]"
                  >
                    {t(c)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {!loading && users.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-[14px] text-[#64748b]">
                    {t('No users match this filter.')}
                  </td>
                </tr>
              )}
              {users.map((u) => {
                const a = access(u);
                const name = `${u.firstName || ''} ${u.lastName || ''}`.trim() || t('(no name)');
                return (
                  <tr key={u._id} className="border-b border-[#f1f5f9] last:border-0">
                    <td className="py-3 pr-4 text-[14px] font-medium text-[#0f172a]">
                      {name}
                      {u.isActive === false && (
                        <span
                          title={t(
                            'Locked out: cannot log in, and any existing session is rejected on every request'
                          )}
                          className="ml-2 inline-block px-2 py-0.5 rounded-full border border-[#d6dce8] bg-[#f1f5f9] text-[11px] font-semibold text-[#64748b] align-middle"
                        >
                          {t('Deactivated')}
                        </span>
                      )}
                    </td>
                    <td className="py-3 pr-4 text-[13px] text-[#64748b] break-all">{u.email}</td>
                    <td className="py-3 pr-4">
                      <span
                        className={`inline-block px-2.5 py-1 rounded-full border text-[12px] font-semibold ${
                          ROLE_STYLE[u.role] || ROLE_STYLE.patient
                        }`}
                      >
                        {isDistributorAdmin(u)
                          ? t('Distributor admin')
                          : t(ROLE_LABEL[u.role]) || u.role}
                      </span>
                    </td>
                    <td className="py-3 pr-4 text-[13px] text-[#64748b]">
                      {t(
                        u.distributor || isDistributorAdmin(u) ? 'Distributor' : 'Direct MedEffects'
                      )}
                    </td>
                    <td
                      className={`py-3 text-[13px] ${a.broken ? 'text-[#de524c] font-medium' : 'text-[#64748b]'}`}
                      title={a.title || undefined}
                    >
                      {a.text}
                    </td>
                    <td className="py-3 pl-4 text-right whitespace-nowrap">
                      <a
                        href={`/registrations/${encodeURIComponent(u._id)}`}
                        aria-label={t('Edit account for {name}', { name })}
                        className="inline-flex min-h-[38px] items-center rounded-lg px-3 text-sm font-semibold text-[#0069c4] hover:bg-[#e6f1ff] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#0089ff]"
                      >
                        {t('Edit account')}
                      </a>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-5">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="h-[38px] px-4 border border-[#d6dce8] rounded-[8px] text-sm text-[#24315d] disabled:opacity-40"
            >
              {t('Previous')}
            </button>
            <span className="text-[13px] text-[#64748b]">
              {t('Page {page} of {total}', { page, total: totalPages })}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="h-[38px] px-4 border border-[#d6dce8] rounded-[8px] text-sm text-[#24315d] disabled:opacity-40"
            >
              {t('Next')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
